import React, { forwardRef, ComponentType } from "react";
import styles from "./index.module.scss";
import classNames from "classnames";

// 组件 Props 类型定义
export interface AntdCusProps {
  /** Ant Design 组件元素 */
  Element: ComponentType<any>;
  /** 是否不使用包装器 */
  noWrapper?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 子元素 */
  children?: React.ReactNode;
  /** 无障碍支持 - 角色 */
  role?: string;
  /** 无障碍支持 - 标签 */
  "aria-label"?: string;
  /** 无障碍支持 - 标签ID */
  "aria-labelledby"?: string;
  /** 无障碍支持 - 描述ID */
  "aria-describedby"?: string;
  /** 其他属性 */
  [key: string]: any;
}

// 组件类型定义
export interface AntdCusComponent
  extends React.ForwardRefExoticComponent<
    AntdCusProps & React.RefAttributes<any>
  > {
  displayName?: string;
}

/**
 * AntdCus 组件 - 用于自定义 Ant Design 组件样式
 *
 * 功能特性：
 * - 支持 TypeScript 类型检查
 * - 移动端优化
 * - 主题支持
 * - 无障碍支持
 * - 设计令牌集成
 *
 * @param Element - Ant Design 组件
 * @param noWrapper - 是否不使用包装器
 * @param className - 自定义类名
 * @param children - 子元素
 * @param props - 其他属性
 * @param ref - 引用
 */
const AntdCus = forwardRef<any, AntdCusProps>(
  ({ Element, noWrapper = false, className, children, ...props }, ref) => {
    // 验证 Element 是否为有效的 React 组件
    if (
      !Element ||
      (typeof Element !== "function" && typeof Element !== "string")
    ) {
      // 在开发环境下提供更详细的调试信息
      if (process.env.NODE_ENV === "development") {
        console.warn("AntdCus: Element prop must be a valid React component", {
          Element,
          ElementType: typeof Element,
          ElementConstructor: Element?.constructor?.name,
          Stack: new Error().stack,
        });
      }
      return null;
    }

    // 构建无障碍属性
    const accessibilityProps = {
      role: props.role || "presentation",
      "aria-label": props["aria-label"],
      "aria-labelledby": props["aria-labelledby"],
      "aria-describedby": props["aria-describedby"],
    };

    // 确保 Element 是一个有效的 React 组件
    const ValidElement = Element as ComponentType<any>;

    if (noWrapper) {
      return (
        <ValidElement
          className={classNames(className, styles.antdCusRoot)}
          {...accessibilityProps}
          {...props}
          ref={ref}
        >
          {children}
        </ValidElement>
      );
    } else {
      return (
        <div className={styles.antdCusRoot} {...accessibilityProps}>
          <ValidElement ref={ref} className={className} {...props}>
            {children}
          </ValidElement>
        </div>
      );
    }
  }
) as AntdCusComponent;

// 设置显示名称用于调试
AntdCus.displayName = "AntdCus";

export default AntdCus;
